/**
 * Projects API client -- typed fetch functions for /api/v1/projects.
 *
 * Follows the same pattern as services/chats.ts.
 */

import { apiClient } from '@/services/api-client';
import type {
  Project,
  ProjectDetailed,
  CreateProjectInput,
  UpdateProjectInput,
  ListProjectsParams,
  ProjectsPage,
  ImportProjectResponse,
} from '@/types/code.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQueryString(params: object): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null,
  );
  if (entries.length === 0) return '';
  const qs = new URLSearchParams();
  for (const [key, value] of entries) {
    qs.set(key, String(value));
  }
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

const BASE = '/v1/projects';

/**
 * List user's projects with cursor-based pagination.
 */
export async function listProjects(
  params?: ListProjectsParams,
): Promise<ProjectsPage> {
  const qs = buildQueryString(params ?? {});
  return apiClient.get<ProjectsPage>(`${BASE}${qs}`);
}

/**
 * Get a single project by ID.
 */
export async function getProject(id: string): Promise<ProjectDetailed> {
  return apiClient.get<ProjectDetailed>(`${BASE}/${id}`);
}

/**
 * Create a new project.
 */
export async function createProject(
  input: CreateProjectInput,
): Promise<Project> {
  return apiClient.post<Project>(BASE, input);
}

/**
 * Update a project.
 */
export async function updateProject(
  id: string,
  input: UpdateProjectInput,
): Promise<Project> {
  return apiClient.patch<Project>(`${BASE}/${id}`, input);
}

/**
 * Archive (soft-delete) a project.
 */
export async function archiveProject(id: string): Promise<void> {
  return apiClient.delete<void>(`${BASE}/${id}`);
}

/**
 * Import a project from a GitHub repository.
 *
 * Creates the project, clones the repo into the workspace, and starts an
 * onboarding chat where the in-VM agent explores and documents the code.
 *
 * @param repoFullName - GitHub "owner/repo" string.
 * @param projectName - Optional override for the project display name.
 * @returns Import result with project, chat_id, and SSE stream_url.
 */
export async function importProjectFromGitHub(
  repoFullName: string,
  projectName?: string,
): Promise<ImportProjectResponse> {
  return apiClient.post<ImportProjectResponse>(
    '/v1/build/projects/import',
    {
      repo_full_name: repoFullName,
      ...(projectName ? { project_name: projectName } : {}),
    },
  );
}

// ---------------------------------------------------------------------------
// VibeHosting publish (deploy the project to opus infra)
// ---------------------------------------------------------------------------

export type PublishStatus =
  | 'idle'
  | 'analyzing'
  | 'building'
  | 'provisioning'
  | 'published'
  | 'removing'
  | 'failed';

export interface PublishState {
  publish_status: PublishStatus;
  app_id?: string | null;
  image_ref?: string | null;
  commit_sha?: string | null;
  public_url?: string | null;
  subdomain?: string | null;
  last_published_at?: string | null;
  last_error?: string | null;
  health_pending?: boolean;
  /**
   * Live boot phase of the app VM while provisioning: firewall | tls-setup |
   * ghcr-login | image-pull | compose-up | done | failed-<phase>. Null/absent
   * outside a boot.
   */
  boot_phase?: string | null;
  updated_at?: string;
}

export interface VmSizingOverride {
  vcpu?: number;
  ram_mb?: number;
  storage_gb?: number;
}

/** One-button publish: everything optional (advanced settings only). */
export interface PublishRequest {
  sizing?: { app?: VmSizingOverride; data?: VmSizingOverride };
  env?: Record<string, string>;
}

/** A host VM size the user can pick from (a CloudStack compute offering). */
export interface PublishOffering {
  id: string;
  name: string;
  description: string;
  vcpu: number;
  ram_mb: number;
}

/** Start a publish (build image via CI -> deploy on opus infra). Returns immediately. */
export async function publishProject(
  projectId: string,
  body: PublishRequest,
): Promise<PublishState> {
  return apiClient.post<PublishState>(`/v1/build/projects/${projectId}/publish`, body);
}

/** Poll the current publish state for a project. */
export async function getPublishStatus(projectId: string): Promise<PublishState> {
  return apiClient.get<PublishState>(`/v1/build/projects/${projectId}/publish/status`);
}

/** List the host VM sizes (compute offerings) available to the org. */
export async function getPublishOfferings(): Promise<PublishOffering[]> {
  const res = await apiClient.get<{ data: PublishOffering[] }>('/v1/build/publish/offerings');
  return res.data ?? [];
}

/**
 * One VM backing the published app. `vm_id` is an internal identifier used
 * only for API calls -- the UI must always display `label`/`name` instead.
 */
export interface PublishInstance {
  vm_id: string;
  name: string;
  role: string;
  label: string;
  state: string;
  private_ip?: string | null;
}

export interface PublishInstancesResponse {
  instances: PublishInstance[];
}

/** List the VMs backing the current publish. */
export async function getPublishInstances(
  projectId: string,
): Promise<PublishInstancesResponse> {
  return apiClient.get<PublishInstancesResponse>(
    `/v1/build/projects/${projectId}/publish/instances`,
  );
}

export type PublishInstanceAction = 'start' | 'stop';

/** Start/stop one instance of the published app. */
export async function publishInstanceAction(
  projectId: string,
  vmId: string,
  action: PublishInstanceAction,
): Promise<void> {
  return apiClient.post<void>(
    `/v1/build/projects/${projectId}/publish/instances/${vmId}/action`,
    { action },
  );
}

/**
 * Unpublish (tear down) the published app: all instances, public IP and DNS
 * are removed. The publish status transitions to 'removing' while the
 * teardown runs.
 */
export async function unpublishProject(projectId: string): Promise<void> {
  return apiClient.delete<void>(`/v1/build/projects/${projectId}/publish`);
}
